import { useEffect, useMemo, useState, useCallback } from "react";
import { Plus, ChevronLeft, ChevronRight, Calendar as CalIcon, Filter, Users as UsersIcon, ListTodo, ExternalLink } from "lucide-react";
import { Link } from "react-router-dom";
import { toast } from "sonner";
import api from "@/lib/api";
import { useAuth } from "@/contexts/AuthContext";
import { AppHeader } from "@/components/AppHeader";
import { EventModal } from "@/components/EventModal";

const DOW_DE = ["Mo", "Di", "Mi", "Do", "Fr", "Sa", "So"];
const MONTH_DE = ["Januar","Februar","März","April","Mai","Juni","Juli","August","September","Oktober","November","Dezember"];

// Returns 6-week grid (42 days) starting Monday before/at the 1st of `month`.
const buildMonthGrid = (year, month) => {
  const first = new Date(year, month, 1);
  const dayOfWeek = (first.getDay() + 6) % 7; // 0=Mon
  const start = new Date(year, month, 1 - dayOfWeek);
  const days = [];
  for (let i = 0; i < 42; i++) {
    const d = new Date(start);
    d.setDate(start.getDate() + i);
    days.push(d);
  }
  return days;
};

const sameDay = (a, b) =>
  a.getFullYear() === b.getFullYear() && a.getMonth() === b.getMonth() && a.getDate() === b.getDate();

const formatTime = (iso) => {
  const d = new Date(iso);
  return `${String(d.getHours()).padStart(2,"0")}:${String(d.getMinutes()).padStart(2,"0")}`;
};

export default function CalendarPage() {
  const { user } = useAuth();
  const today = new Date();
  const [cursor, setCursor] = useState(new Date(today.getFullYear(), today.getMonth(), 1));
  const [view, setView] = useState("month"); // month | week
  const [events, setEvents] = useState([]);
  const [taskEvents, setTaskEvents] = useState([]);
  const [config, setConfig] = useState({ categories: [], members: [] });
  const [memberFilter, setMemberFilter] = useState("");  // empty = all visible
  const [categoryFilter, setCategoryFilter] = useState({}); // {key: bool}
  const [modalOpen, setModalOpen] = useState(false);
  const [modalDate, setModalDate] = useState(null);
  const [editEvent, setEditEvent] = useState(null);
  const [dragId, setDragId] = useState(null);
  const [dragOverKey, setDragOverKey] = useState(null);

  const isManager = user?.role === "admin" || user?.role === "sales_manager";

  const [from, to] = useMemo(() => {
    if (view === "month") {
      const grid = buildMonthGrid(cursor.getFullYear(), cursor.getMonth());
      const first = grid[0];
      const last = grid[grid.length - 1];
      const start = new Date(first.getFullYear(), first.getMonth(), first.getDate(), 0, 0, 0);
      const end = new Date(last.getFullYear(), last.getMonth(), last.getDate(), 23, 59, 59);
      return [start.toISOString(), end.toISOString()];
    }
    // week
    const dow = (cursor.getDay() + 6) % 7;
    const start = new Date(cursor); start.setDate(cursor.getDate() - dow); start.setHours(0,0,0,0);
    const end = new Date(start); end.setDate(start.getDate() + 6); end.setHours(23,59,59,999);
    return [start.toISOString(), end.toISOString()];
  }, [cursor, view]);

  const loadConfig = useCallback(async () => {
    try {
      const { data } = await api.get("/calendar/config");
      setConfig(data);
      setCategoryFilter((prev) => {
        const next = { ...prev };
        (data.categories || []).forEach((c) => { if (next[c.key] === undefined) next[c.key] = true; });
        if (next.task === undefined) next.task = true;
        return next;
      });
    } catch {}
  }, []);

  const loadEvents = useCallback(async () => {
    try {
      const params = { from, to };
      if (memberFilter) params.user_id = memberFilter;
      const { data } = await api.get("/calendar/events", { params });
      setEvents(data.events || []);
      setTaskEvents(data.tasks || []);
    } catch {}
  }, [from, to, memberFilter]);

  useEffect(() => { loadConfig(); }, [loadConfig]);
  useEffect(() => { loadEvents(); }, [loadEvents]);

  const allEvents = useMemo(() => {
    const list = [...events, ...taskEvents];
    return list.filter((e) => {
      const key = e.is_task ? "task" : e.category;
      return categoryFilter[key] !== false;
    });
  }, [events, taskEvents, categoryFilter]);

  const eventsByDay = useMemo(() => {
    const map = new Map();
    allEvents.forEach((e) => {
      const d = new Date(e.starts_at);
      const k = `${d.getFullYear()}-${d.getMonth()}-${d.getDate()}`;
      if (!map.has(k)) map.set(k, []);
      map.get(k).push(e);
    });
    for (const [, arr] of map) arr.sort((a, b) => a.starts_at.localeCompare(b.starts_at));
    return map;
  }, [allEvents]);

  const openNew = (date) => {
    setEditEvent(null);
    setModalDate(date ? date.toISOString() : new Date().toISOString());
    setModalOpen(true);
  };

  const openEdit = (ev) => {
    if (ev.is_task) return; // tasks edited on tasks page
    setEditEvent(ev);
    setModalOpen(true);
  };

  const handleDropOnDay = async (targetDate) => {
    const ev = allEvents.find((e) => e.id === dragId);
    setDragId(null);
    setDragOverKey(null);
    if (!ev || ev.is_task) return;
    const orig = new Date(ev.starts_at);
    const end = new Date(ev.ends_at);
    const durationMs = end - orig;
    const newStart = new Date(targetDate);
    newStart.setHours(orig.getHours(), orig.getMinutes(), 0, 0);
    const newEnd = new Date(newStart.getTime() + durationMs);
    // No-op if same day
    if (newStart.toDateString() === orig.toDateString()) return;
    // Optimistic update
    setEvents((list) => list.map((e) => e.id === ev.id ? { ...e, starts_at: newStart.toISOString(), ends_at: newEnd.toISOString() } : e));
    try {
      await api.patch(`/calendar/events/${ev.id}`, {
        starts_at: newStart.toISOString(),
        ends_at: newEnd.toISOString(),
      });
      toast.success("Termin verschoben");
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Verschieben fehlgeschlagen");
      loadEvents();
    }
  };

  const monthGrid = useMemo(() => buildMonthGrid(cursor.getFullYear(), cursor.getMonth()), [cursor]);
  const weekDays = useMemo(() => {
    const dow = (cursor.getDay() + 6) % 7;
    const start = new Date(cursor); start.setDate(cursor.getDate() - dow);
    return Array.from({ length: 7 }, (_, i) => {
      const d = new Date(start); d.setDate(start.getDate() + i); return d;
    });
  }, [cursor]);

  const goPrev = () => {
    if (view === "month") setCursor(new Date(cursor.getFullYear(), cursor.getMonth() - 1, 1));
    else { const d = new Date(cursor); d.setDate(d.getDate() - 7); setCursor(d); }
  };
  const goNext = () => {
    if (view === "month") setCursor(new Date(cursor.getFullYear(), cursor.getMonth() + 1, 1));
    else { const d = new Date(cursor); d.setDate(d.getDate() + 7); setCursor(d); }
  };
  const goToday = () => setCursor(new Date(today.getFullYear(), today.getMonth(), today.getDate()));

  const title = view === "month"
    ? `${MONTH_DE[cursor.getMonth()]} ${cursor.getFullYear()}`
    : `Woche ${weekDays[0].getDate()}.${weekDays[0].getMonth() + 1}. – ${weekDays[6].getDate()}.${weekDays[6].getMonth() + 1}.${weekDays[6].getFullYear()}`;

  return (
    <div className="min-h-screen bg-[#09090B]" data-testid="calendar-page">
      <AppHeader title="Kalender" back="/dashboard" />

      <main className="max-w-6xl mx-auto px-3 sm:px-4 py-4 sm:py-6">
        {/* Toolbar */}
        <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-3 mb-4">
          <div className="flex items-center gap-2">
            <button onClick={goPrev} data-testid="cal-prev-btn" className="h-10 w-10 rounded-xl bg-[#18181B] border border-zinc-800 hover:border-zinc-700 flex items-center justify-center"><ChevronLeft size={18} /></button>
            <button onClick={goToday} data-testid="cal-today-btn" className="h-10 px-3 rounded-xl bg-[#18181B] border border-zinc-800 hover:border-[#00E575]/50 text-sm">Heute</button>
            <button onClick={goNext} data-testid="cal-next-btn" className="h-10 w-10 rounded-xl bg-[#18181B] border border-zinc-800 hover:border-zinc-700 flex items-center justify-center"><ChevronRight size={18} /></button>
            <h2 className="font-display text-lg sm:text-xl font-semibold ml-2 truncate" data-testid="cal-title">{title}</h2>
          </div>
          <div className="flex items-center gap-2 flex-wrap">
            <div className="flex rounded-xl bg-[#18181B] border border-zinc-800 overflow-hidden">
              <button onClick={() => setView("month")} data-testid="cal-view-month" className={`px-3 h-10 text-sm ${view==="month"?"bg-[#00E575] text-[#09090B] font-semibold":"text-zinc-300 hover:bg-zinc-800"}`}>Monat</button>
              <button onClick={() => setView("week")} data-testid="cal-view-week" className={`px-3 h-10 text-sm ${view==="week"?"bg-[#00E575] text-[#09090B] font-semibold":"text-zinc-300 hover:bg-zinc-800"}`}>Woche</button>
            </div>
            <Link to="/tasks" data-testid="cal-link-tasks" className="h-10 inline-flex items-center gap-2 px-3 rounded-xl bg-[#18181B] border border-zinc-800 hover:border-[#00E575]/50 text-sm">
              <ListTodo size={16} /> Aufgaben
            </Link>
            <Link
              to="/profile?ics=1"
              data-testid="cal-external-sync-btn"
              title="Outlook · Google · Apple Kalender abonnieren"
              className="h-10 inline-flex items-center gap-2 px-3 rounded-xl bg-[#18181B] border border-zinc-800 hover:border-[#00E575]/50 text-sm"
            >
              <ExternalLink size={14} /> <span className="hidden sm:inline">Externe Sync</span><span className="sm:hidden">Sync</span>
            </Link>
            <button onClick={() => openNew(new Date())} data-testid="cal-new-event-btn" className="h-10 inline-flex items-center gap-2 px-3 rounded-xl bg-[#00E575] text-[#09090B] font-semibold text-sm hover:bg-[#00C964]">
              <Plus size={16} /> Termin
            </button>
          </div>
        </div>

        {/* Filters */}
        <div className="flex flex-wrap gap-2 mb-4 items-center">
          <Filter size={14} className="text-zinc-500" />
          {(config.categories || []).map((c) => {
            const active = categoryFilter[c.key] !== false;
            return (
              <button
                key={c.key}
                data-testid={`cal-filter-${c.key}`}
                onClick={() => setCategoryFilter((f) => ({ ...f, [c.key]: !active }))}
                className={`text-xs px-2.5 py-1 rounded-lg border flex items-center gap-1.5 transition ${active ? "border-zinc-600 text-zinc-100" : "border-zinc-800 text-zinc-300 opacity-50"}`}
              >
                <span className="w-2 h-2 rounded-full" style={{ backgroundColor: c.color }} />
                {c.label}
              </button>
            );
          })}
          <button
            data-testid="cal-filter-task"
            onClick={() => setCategoryFilter((f) => ({ ...f, task: !(f.task !== false) }))}
            className={`text-xs px-2.5 py-1 rounded-lg border flex items-center gap-1.5 transition ${(categoryFilter.task !== false) ? "border-zinc-600 text-zinc-100" : "border-zinc-800 text-zinc-300 opacity-50"}`}
          >
            <span className="w-2 h-2 rounded-full bg-orange-500" />
            Aufgaben fällig
          </button>
          {isManager && config.members?.length > 0 && (
            <div className="ml-auto flex items-center gap-2">
              <UsersIcon size={14} className="text-zinc-500" />
              <select
                value={memberFilter}
                onChange={(e) => setMemberFilter(e.target.value)}
                data-testid="cal-member-filter"
                className="bg-[#18181B] border border-zinc-800 rounded-lg px-2 h-9 text-xs outline-none focus:border-[#00E575]"
              >
                <option value="">Alle Vertrieb</option>
                {config.members.map((m) => (
                  <option key={m.id} value={m.id}>{m.name}</option>
                ))}
              </select>
            </div>
          )}
        </div>

        {/* Calendar grid */}
        {view === "month" ? (
          <div className="bg-[#18181B] border border-zinc-800 rounded-2xl overflow-hidden">
            <div className="grid grid-cols-7 border-b border-zinc-800 bg-[#0E0E12]">
              {DOW_DE.map((d) => (
                <div key={d} className="px-2 py-2 text-[12px] font-mono uppercase tracking-wider text-zinc-300 text-center">{d}</div>
              ))}
            </div>
            <div className="grid grid-cols-7" data-testid="cal-month-grid">
              {monthGrid.map((d, i) => {
                const inMonth = d.getMonth() === cursor.getMonth();
                const isToday = sameDay(d, today);
                const key = `${d.getFullYear()}-${d.getMonth()}-${d.getDate()}`;
                const dayEvents = eventsByDay.get(key) || [];
                const isDropTarget = dragOverKey === key;
                return (
                  <div
                    key={i}
                    data-testid={`cal-day-${d.getDate()}`}
                    onClick={() => openNew(d)}
                    onDragOver={(e) => { if (dragId) { e.preventDefault(); setDragOverKey(key); } }}
                    onDragLeave={() => { if (dragOverKey === key) setDragOverKey(null); }}
                    onDrop={(e) => { e.preventDefault(); handleDropOnDay(d); }}
                    className={`min-h-[88px] sm:min-h-[110px] border-r border-b border-zinc-800/80 last:border-r-0 p-1.5 cursor-pointer transition relative ${inMonth ? "bg-[#18181B] hover:bg-[#1F1F23]" : "bg-[#101013] text-zinc-600"} ${isDropTarget ? "ring-2 ring-[#00E575] ring-inset" : ""}`}
                  >
                    <div className={`text-xs font-mono inline-flex items-center justify-center h-6 w-6 rounded-md ${isToday ? "bg-[#00E575] text-[#09090B] font-bold" : "text-zinc-400"}`}>
                      {d.getDate()}
                    </div>
                    <div className="mt-1 space-y-1">
                      {dayEvents.slice(0, 3).map((ev) => (
                        <button
                          key={ev.id}
                          draggable={!ev.is_task}
                          onDragStart={(e) => { if (ev.is_task) return; setDragId(ev.id); e.dataTransfer.effectAllowed = "move"; }}
                          onDragEnd={() => { setDragId(null); setDragOverKey(null); }}
                          onClick={(e) => { e.stopPropagation(); openEdit(ev); }}
                          data-testid={`cal-event-${ev.id}`}
                          className={`block w-full text-left text-[12px] px-1.5 py-1 rounded-md text-zinc-900 font-medium truncate hover:opacity-90 ${dragId === ev.id ? "opacity-50" : ""}`}
                          style={{ backgroundColor: ev.color }}
                          title={`${ev.title}${ev.all_day ? "" : " · " + formatTime(ev.starts_at)}`}
                        >
                          {!ev.all_day && <span className="font-mono mr-1 opacity-80">{formatTime(ev.starts_at)}</span>}
                          {ev.title}
                        </button>
                      ))}
                      {dayEvents.length > 3 && (
                        <div className="text-[12px] text-zinc-300 px-1.5">+{dayEvents.length - 3} weitere</div>
                      )}
                    </div>
                  </div>
                );
              })}
            </div>
          </div>
        ) : (
          <div className="bg-[#18181B] border border-zinc-800 rounded-2xl overflow-hidden">
            <div className="grid grid-cols-7 border-b border-zinc-800 bg-[#0E0E12]">
              {weekDays.map((d, i) => (
                <div key={i} className="px-2 py-2 text-center">
                  <div className="text-[12px] font-mono uppercase tracking-wider text-zinc-300">{DOW_DE[i]}</div>
                  <div className={`text-sm font-semibold ${sameDay(d, today) ? "text-[#00E575]" : "text-zinc-200"}`}>{d.getDate()}.{d.getMonth() + 1}</div>
                </div>
              ))}
            </div>
            <div className="grid grid-cols-7 min-h-[480px]" data-testid="cal-week-grid">
              {weekDays.map((d, i) => {
                const key = `${d.getFullYear()}-${d.getMonth()}-${d.getDate()}`;
                const dayEvents = eventsByDay.get(key) || [];
                return (
                  <div
                    key={i}
                    onClick={() => openNew(d)}
                    className="border-r border-zinc-800/80 last:border-r-0 p-2 cursor-pointer hover:bg-[#1F1F23] transition space-y-2"
                  >
                    {dayEvents.length === 0 && <div className="text-[12px] text-zinc-400 italic">—</div>}
                    {dayEvents.map((ev) => (
                      <button
                        key={ev.id}
                        onClick={(e) => { e.stopPropagation(); openEdit(ev); }}
                        data-testid={`cal-event-${ev.id}`}
                        className="block w-full text-left text-xs px-2 py-1.5 rounded-md text-zinc-900 font-medium hover:opacity-90"
                        style={{ backgroundColor: ev.color }}
                      >
                        {!ev.all_day && <div className="font-mono text-[12px] opacity-80">{formatTime(ev.starts_at)}–{formatTime(ev.ends_at)}</div>}
                        <div className="truncate">{ev.title}</div>
                        {ev.location && <div className="truncate opacity-80 text-[12px]">{ev.location}</div>}
                      </button>
                    ))}
                  </div>
                );
              })}
            </div>
          </div>
        )}

        {/* Upcoming list (mobile-friendly summary) */}
        <div className="mt-6">
          <h3 className="font-display text-base font-semibold mb-3 flex items-center gap-2"><CalIcon size={16} /> Bevorstehend</h3>
          <div className="space-y-2">
            {allEvents
              .filter((e) => new Date(e.starts_at) >= new Date(today.toDateString()))
              .slice(0, 5)
              .map((ev) => (
                <button
                  key={ev.id}
                  onClick={() => openEdit(ev)}
                  data-testid={`cal-upcoming-${ev.id}`}
                  className="w-full bg-[#18181B] border border-zinc-800 rounded-xl p-3 hover:border-zinc-700 text-left flex items-center gap-3"
                >
                  <div className="w-1.5 h-12 rounded-full flex-shrink-0" style={{ backgroundColor: ev.color }} />
                  <div className="min-w-0 flex-1">
                    <div className="font-medium truncate">{ev.title}</div>
                    <div className="text-xs text-zinc-400 font-mono">
                      {new Date(ev.starts_at).toLocaleDateString("de-DE", { weekday: "short", day: "2-digit", month: "2-digit" })}
                      {!ev.all_day && ` · ${formatTime(ev.starts_at)}`}
                      {ev.location && ` · ${ev.location}`}
                    </div>
                  </div>
                </button>
              ))}
            {allEvents.length === 0 && (
              <div className="bg-[#18181B] border border-zinc-800 rounded-xl p-6 text-center text-zinc-400 text-sm font-medium">Keine Termine im aktuellen Zeitraum.</div>
            )}
          </div>
        </div>
      </main>

      <EventModal
        open={modalOpen}
        onClose={() => setModalOpen(false)}
        initialDate={modalDate}
        event={editEvent}
        categories={config.categories}
        members={config.members}
        onSaved={loadEvents}
      />
    </div>
  );
}
